// Sun position + golden-hour helpers built on suncalc.
//
// suncalc returns altitude/azimuth in radians and key day-times as Date
// objects. We re-export degrees for ease of use in scoring + UI, plus
// derived helpers like "is this Date inside golden hour?" and "when's
// the next golden hour after this Date?".

import SunCalc from 'suncalc';

const RAD_TO_DEG = 180 / Math.PI;

export function sunPosition(date, lat, lng) {
  const { altitude, azimuth } = SunCalc.getPosition(date, lat, lng);
  return {
    altitudeDeg: altitude * RAD_TO_DEG,
    azimuthDeg: azimuth * RAD_TO_DEG,
  };
}

// suncalc named day-times. Most useful for us:
//   sunrise / sunset             — disc on horizon
//   sunriseEnd / sunsetStart     — disc fully visible / starts setting
//   goldenHourEnd                — end of morning golden hour
//   goldenHour                   — start of evening golden hour
//   dawn / dusk                  — civil twilight
//   nauticalDawn / nauticalDusk
//   night / nightEnd
export function sunTimes(date, lat, lng) {
  return SunCalc.getTimes(date, lat, lng);
}

// Returns a pair of {start, end} ranges describing today's morning and
// evening golden hours at the given location. Either side can be null
// (e.g. polar day/night).
export function goldenHoursFor(date, lat, lng) {
  const t = sunTimes(date, lat, lng);
  const morning = isFiniteDate(t.sunrise) && isFiniteDate(t.goldenHourEnd)
    ? { start: t.sunrise, end: t.goldenHourEnd, label: 'morning_golden' }
    : null;
  const evening = isFiniteDate(t.goldenHour) && isFiniteDate(t.sunset)
    ? { start: t.goldenHour, end: t.sunset, label: 'evening_golden' }
    : null;
  return { morning, evening };
}

export function isGoldenHour(date, lat, lng) {
  const { morning, evening } = goldenHoursFor(date, lat, lng);
  const t = date.getTime();
  if (morning && t >= morning.start.getTime() && t <= morning.end.getTime()) return 'morning_golden';
  if (evening && t >= evening.start.getTime() && t <= evening.end.getTime()) return 'evening_golden';
  return null;
}

// Walks forward up to 3 days looking for the next golden-hour window
// strictly after `from`. Useful for Today's "next opportunity" surfacing.
export function nextGoldenHour(from, lat, lng) {
  for (let dayOffset = 0; dayOffset < 3; dayOffset += 1) {
    const day = new Date(from.getTime() + dayOffset * 86400_000);
    const { morning, evening } = goldenHoursFor(day, lat, lng);
    for (const win of [morning, evening]) {
      if (win && win.start.getTime() > from.getTime()) return win;
    }
  }
  return null;
}

// Light-quality factor 0..1 driven by sun altitude alone (not cloud
// modulated — that's combined in scoring.js).
//   < 0deg     : 0      (sun below horizon)
//   0..6deg    : 0.5    (twilight ramp)
//   6..15deg   : 0.95   (golden-hour band — soft, warm, low)
//   15..40deg  : 0.7    (good but harsher)
//   40..60deg  : 0.55   (high sun, harsh shadows)
//   > 60deg    : 0.45   (overhead — very harsh, only OK with diffuse cloud)
export function altitudeQuality(altitudeDeg) {
  if (altitudeDeg < 0) return 0;
  if (altitudeDeg < 6) return 0.4 + (altitudeDeg / 6) * 0.55;
  if (altitudeDeg < 15) return 0.95 - ((altitudeDeg - 6) / 9) * 0.05;
  if (altitudeDeg < 40) return 0.9 - ((altitudeDeg - 15) / 25) * 0.2;
  if (altitudeDeg < 60) return 0.7 - ((altitudeDeg - 40) / 20) * 0.15;
  return Math.max(0.4, 0.55 - ((altitudeDeg - 60) / 30) * 0.15);
}

function isFiniteDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}
